"use client";

import { Heart, Trash2 } from "lucide-react";

import Price from "@/components/common/Price";
import QuantitySelector from "@/components/common/QuantitySelector";
import Badge from "@/components/common/Badge";
import NextImage from "@/components/common/NextImage";
import { CartItem } from "@/packages/types/cart";
import { VariantAttribute } from "@/packages/types/products";


interface CartItemRowProps {
  item: CartItem;
  onProductClick?: (item: CartItem) => void;
  onUpdateQty: (id: number | string, quantity: number, variantId?: number) => void;
  onRemoveItem: (id: number | string, variantId?: number) => void;
  onToggleWishlist: (item: CartItem) => void;
  isInWishlist: boolean;
}

export default function CartItemRow({
  item,
  onProductClick,
  onUpdateQty,
  onRemoveItem,
  onToggleWishlist,
  isInWishlist,
}: CartItemRowProps) {
  const isFree = String(item.id).includes("-free");

  const variantImage =
    item.variant && item.images
      ? item.images.find((img) => img.id == item.variant?.imageId)
      : null;

  const imageUrl =
    variantImage?.imageUrl ||
    item?.images?.[0]?.imageUrl ||
    "/assets/site/images/default.png";

  const variantAttributes =
    item.variant?.variantAttributes
      ?.map((attr: VariantAttribute) => attr.attribute_value?.name || "")
      .filter(Boolean)
      .join(" - ") || null;

  const stock = item.variant?.stock ?? item.stock ?? Infinity;
  const isStockAvailable = stock > 0;

  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(item);
    }
  };


  return (
    <div className="p-3 bg-white border border-slate-100 rounded-xl flex gap-3 items-start shadow-xs hover:border-slate-200 transition-all">
      {/* Product Image */}
      <div
        onClick={handleProductClick}
        className="w-16 h-16 rounded-lg border border-slate-100 overflow-hidden shrink-0 cursor-pointer bg-slate-50"
      >
        <NextImage
          src={imageUrl?.trim() || "/assets/site/images/default.png"}
          alt={item.title}
          width={64}
          height={64}
          className="w-full h-full object-contain p-1"
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex justify-between items-start gap-2">
          <h4
            onClick={handleProductClick}
            className="text-xs font-bold text-slate-800 line-clamp-2 cursor-pointer hover:text-[var(--brand-accent)] transition-colors leading-snug"
          >
            {item.title}
          </h4>
        </div>

        {variantAttributes && (
          <p className="text-[11px] text-slate-400 font-medium">{variantAttributes}</p>
        )}

        {/* Price & Unit Breakdown */}
        {!isFree && (
          <div className="flex items-baseline gap-1 text-xs">
            <Price
              amount={Number(item.finalPrice ?? item.price)}
              className="font-black text-slate-900"
            />
            <span className="text-[10px] text-slate-400 font-normal">
              / unit · x{item.quantity}
            </span>
          </div>
        )}

        {/* Action Row: Quantity + Stock Badge + Wishlist + Delete */}
        <div className="flex items-center justify-between pt-1 gap-2 flex-wrap">
          {!isFree ? (
            <>
              {/* Quantity Selector */}
              {isStockAvailable ? (
                <QuantitySelector
                  value={item.quantity}
                  min={1}
                  max={stock}
                  onChange={(newQty) =>
                    onUpdateQty(item.id, newQty, item.variant?.id)
                  }
                  className="scale-90 origin-left border border-slate-200 rounded-lg"
                />
              ) : (
                <Badge text="Out of stock" className="bg-rose-500 text-white text-[10px]" />
              )}

              <div className="flex items-center gap-1.5 ml-auto">
                {/* Wishlist Button */}
                <button
                  type="button"
                  onClick={() => onToggleWishlist(item)}
                  aria-label="Wishlist toggle"
                  className="p-1 text-slate-400 hover:text-rose-500 transition-colors cursor-pointer"
                >
                  <Heart className={`w-4 h-4 ${isInWishlist ? "text-rose-500 fill-current" : ""}`} />
                </button>

                {/* Trash Button */}
                <button
                  type="button"
                  onClick={() => onRemoveItem(item.id, item.variant?.id)}
                  aria-label={`Remove ${item.title}`}
                  className="p-1 text-slate-400 hover:text-rose-600 transition-colors cursor-pointer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <span className="text-xs text-emerald-600 font-bold">
              🎁 Free Item (Qty: {item.quantity})
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
